import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AppVersion, TranslationService } from '@core';
import { SzWhatsNewDialogService } from '@ui/modal';
import { WhatsNewDataService } from '@update/infra';
import { WhatsNewEntry, WhatsNewFile, WhatsNewTranslation, WhatsNewVersionView } from '../models/whats-new.model';

const LAST_SEEN_KEY: string = 'splitzero.whatsNew.lastSeenVersion';
const FALLBACK_LANG: string = 'en';
const CTA_KEY: string = 'whatsNew.gotIt';

@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly data: WhatsNewDataService = inject(WhatsNewDataService);
  private readonly dialog: SzWhatsNewDialogService = inject(SzWhatsNewDialogService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly translation: TranslationService = inject(TranslationService);

  async maybeShow(): Promise<void> {
    // Wpisy whats-new są kluczowane po „kamieniu milowym minor" (X.Y.0), nie po dokładnej wersji:
    // patche są przezroczyste, a modal wyzwala dopiero wejście na NOWY minor. Patrz ADR-0015.
    const lastSeenRaw: string | null = localStorage.getItem(LAST_SEEN_KEY);
    const milestone: string = this.milestoneOf(AppVersion);
    const lastSeenMilestone: string | null = lastSeenRaw === null ? null : this.milestoneOf(lastSeenRaw);

    // Guard "ten (lub nowszy) minor już widziany" najpierw - Today bywa odwiedzane wielokrotnie,
    // nie strzelamy po sieci bez potrzeby. Normalizacja starych wartości (exact) do minora = brak migracji.
    if (lastSeenMilestone !== null && this.compareVersions(milestone, lastSeenMilestone) <= 0) return;

    const file: WhatsNewFile | null = await this.data.fetch();
    if (!file) return; // brak / błąd / zepsuty plik - cicho

    const entry: WhatsNewEntry | undefined = file[milestone];
    if (!entry) return; // ten minor nie ma wpisu - cicho, bez błędu
    if (!entry.showModal) return; // flaga wyłączona dla tego minora

    // Świeża instalacja: nowy user, który nie wybrał jeszcze języka (leci do language pickera).
    // Nie pokazujemy mu changelogu minora, którego nigdy nie miał - tylko zapamiętujemy bieżący milestone.
    // Istniejący user bez lastSeen (sprzed feature'u) MA wybrany język → modal mu pokażemy.
    if (lastSeenRaw === null && !this.translation.hasUserPickedLanguage()) {
      this.markSeen(milestone);
      return;
    }

    const translation: WhatsNewTranslation | null = this.pickTranslation(entry);
    if (!translation) return; // brak treści w języku usera i w fallbacku

    const ctaLabel: string = await firstValueFrom(this.translate.get(CTA_KEY));
    // Zapamiętujemy milestone od razu przy pokazaniu - modal nie powtórzy się,
    // nawet jeśli user zamknie appkę bez kliknięcia CTA.
    this.markSeen(milestone);
    this.dialog.open$({ title: translation.title, changes: translation.changes, ctaLabel }).subscribe();
  }

  // Pełna historia dla strony „Co nowego": wszystkie wersje z treścią w języku usera
  // (z fallbackiem na en), posortowane malejąco. Wersje bez wpisu/treści są pomijane,
  // żeby na stronie nie było pustych akordeonów. Klucze meta (np. _comment) ignorowane.
  async loadHistory(): Promise<WhatsNewVersionView[]> {
    const file: WhatsNewFile | null = await this.data.fetch();
    if (!file) return [];

    const lang: string = this.translation.currentLanguage();
    const views: WhatsNewVersionView[] = [];

    for (const version of Object.keys(file)) {
      if (version.startsWith('_')) continue; // meta, np. _comment
      const entry: WhatsNewEntry = file[version];
      const translation: WhatsNewTranslation | null =
        entry.translations[lang] ?? entry.translations[FALLBACK_LANG] ?? null;
      if (!translation || translation.changes.length === 0) continue; // pusta/brak → bez akordeonu
      views.push({
        version,
        date: entry.date ?? null,
        title: translation.title,
        changes: translation.changes
      });
    }

    return views.sort((a: WhatsNewVersionView, b: WhatsNewVersionView): number =>
      this.compareVersionsDesc(a.version, b.version)
    );
  }

  // „Kamień milowy" minor: major.minor.0 (1.1.5 → 1.1.0, „2" → 2.0.0). Patch zerowany,
  // bo patche nie wyzwalają modala - liczy się tylko minor.
  private milestoneOf(version: string): string {
    const parts: number[] = version.split('.').map((n: string): number => Number(n) || 0);
    return `${parts[0] ?? 0}.${parts[1] ?? 0}.0`;
  }

  // Porównanie wersji semver-like rosnąco: >0 gdy a>b, <0 gdy a<b, 0 gdy równe.
  private compareVersions(a: string, b: string): number {
    const pa: number[] = a.split('.').map((n: string): number => Number(n) || 0);
    const pb: number[] = b.split('.').map((n: string): number => Number(n) || 0);
    const len: number = Math.max(pa.length, pb.length);
    for (let i: number = 0; i < len; i++) {
      const diff: number = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  }

  // Malejąco (1.1.0 przed 1.0.9) - do sortowania historii.
  private compareVersionsDesc(a: string, b: string): number {
    return this.compareVersions(b, a);
  }

  private pickTranslation(entry: WhatsNewEntry): WhatsNewTranslation | null {
    const lang: string = this.translation.currentLanguage();
    return entry.translations[lang] ?? entry.translations[FALLBACK_LANG] ?? null;
  }

  private markSeen(milestone: string): void {
    localStorage.setItem(LAST_SEEN_KEY, milestone);
  }
}
